import type { ApiComponents } from '@aztec-realms/api-client';

import { browserApiClient, requiredApiData } from './browser-api';

type LoginResponse = ApiComponents['schemas']['LoginResponse'];
type RegisterRequest = ApiComponents['schemas']['RegisterRequest'];
type RegisterResponse = ApiComponents['schemas']['RegisterResponse'];
type MessageResponse = ApiComponents['schemas']['MessageResponse'];

export async function getRegistrationPolicy() {
  const result = await browserApiClient().GET('/api/v1/auth/registration-policy');
  return requiredApiData(
    result.data,
    result.error,
    'Registration availability could not be checked.',
  );
}

async function preparePublicMutation(): Promise<string> {
  const csrfResult = await browserApiClient().GET('/api/v1/auth/csrf');
  const csrf = requiredApiData(
    csrfResult.data,
    csrfResult.error,
    'A secure account request could not be prepared.',
  );
  return csrf.csrf_token;
}

export async function loginPlayer(email: string, password: string): Promise<LoginResponse> {
  const csrfToken = await preparePublicMutation();
  const loginResult = await browserApiClient({ mutation: true, csrfToken }).POST(
    '/api/v1/auth/login',
    { body: { email, password } },
  );
  return requiredApiData(loginResult.data, loginResult.error, 'Sign-in failed.');
}

export async function registerPlayer(input: RegisterRequest): Promise<RegisterResponse> {
  const csrfToken = await preparePublicMutation();
  const result = await browserApiClient({ mutation: true, csrfToken }).POST(
    '/api/v1/auth/register',
    { body: input },
  );
  return requiredApiData(result.data, result.error, 'Your account could not be created.');
}

export async function requestPlayerVerification(email: string): Promise<MessageResponse> {
  const csrfToken = await preparePublicMutation();
  const result = await browserApiClient({ mutation: true, csrfToken }).POST(
    '/api/v1/auth/request-verification',
    { body: { email } },
  );
  return requiredApiData(
    result.data,
    result.error,
    'A verification message could not be requested.',
  );
}

export async function verifyPlayerEmail(token: string): Promise<MessageResponse> {
  const csrfToken = await preparePublicMutation();
  const result = await browserApiClient({ mutation: true, csrfToken }).POST(
    '/api/v1/auth/verify-email',
    { body: { token } },
  );
  return requiredApiData(result.data, result.error, 'Your email could not be verified.');
}

export async function requestPlayerPasswordReset(email: string): Promise<MessageResponse> {
  const csrfToken = await preparePublicMutation();
  const result = await browserApiClient({ mutation: true, csrfToken }).POST(
    '/api/v1/auth/password-reset/request',
    { body: { email } },
  );
  return requiredApiData(
    result.data,
    result.error,
    'A password reset message could not be requested.',
  );
}

export async function confirmPlayerPasswordReset(
  token: string,
  newPassword: string,
): Promise<MessageResponse> {
  const csrfToken = await preparePublicMutation();
  const result = await browserApiClient({ mutation: true, csrfToken }).POST(
    '/api/v1/auth/password-reset/confirm',
    { body: { token, new_password: newPassword } },
  );
  return requiredApiData(result.data, result.error, 'Your password could not be reset.');
}
